import EverTrue from 'app';
import { createContext, useState, useEffect, useCallback, useContext } from 'react';
import Api from 'entities/helpers/api';
import PropTypes from 'prop-types';
import ErrorLogger from 'entities/helpers/error-log-helper';

const EmailSignaturesContext = createContext({});

const noSignature = {
  id: 0,
  signature_name: 'No Signature',
  signature_body: '',
  default: false,
};

const EmailSignaturesProvider = ({ children, scope = 'user' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState({});
  const [defaultSignature, setDefaultSignature] = useState({});

  const getEmailSignatures = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await Api.OUTREACH.EMAIL_SIGNATURES.get({ params: { scope } });
      const signaturesWithNoSignature = [...res, noSignature];
      setSignatures(signaturesWithNoSignature);

      const currDefaultSignature = res.find((sign) => !!sign.default) || noSignature;
      setDefaultSignature(currDefaultSignature);

      if (!selectedSignature.id && selectedSignature.id !== 0) {
        setSelectedSignature(currDefaultSignature);
      }
    } catch (e) {
      ErrorLogger.error('Error getting email signatures', { extra: { e, page_name: 'Email Signatures Context' } });
      setError('Failed to fetch email signatures');
      EverTrue.Alert.error('There was a problem getting email signatures.');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const addEmailSignature = async (signatureName, signatureBody = '') => {
    setLoading(true);
    setError(null);

    try {
      const newSignature = await Api.OUTREACH.EMAIL_SIGNATURE.post({
        data: JSON.stringify({
          signature_name: signatureName,
          signature_body: signatureBody,
          default: false,
        }),
      });

      setDefaultSignature(newSignature);
      setSelectedSignature(newSignature);
      setSignatures((prevSignatures) => {
        const withoutNoSignature = prevSignatures.filter((sig) => sig.id !== 0);
        return [...withoutNoSignature, newSignature, noSignature];
      });

      EverTrue.Alert.success('Email Signature created successfully');
      return newSignature;
    } catch (e) {
      ErrorLogger.error('Error creating Email Signature', { extra: { e, page_name: 'Email Signatures Context' } });
      setError('Failed to create email signature');
      EverTrue.Alert.error('There was a problem creating the email signature.');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const updateEmailSignature = async (id, updatedSignature) => {
    setLoading(true);
    setError(null);

    try {
      const updatedResponse = await Api.OUTREACH.UPDATE_EMAIL_SIGNATURE.patch({
        urlArgs: { id },
        data: JSON.stringify({ ...updatedSignature }),
      });

      setSignatures((prevSignatures) => prevSignatures.map((sig) => (sig.id === id ? updatedResponse : sig)));

      if (selectedSignature.id !== 0) {
        setSelectedSignature(updatedResponse);
        setDefaultSignature(updatedResponse);
      } else {
        noSignature.default = true;
        setSelectedSignature(noSignature);
        setDefaultSignature(noSignature);
      }
      return updatedResponse;
    } catch (e) {
      ErrorLogger.error('Error updating Email Signature', { extra: { e, page_name: 'Email Signatures Context' } });
      setError('Failed to update email signature');
      EverTrue.Alert.error('There was a problem updating the email signature.');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // Delete an email signature
  const deleteEmailSignature = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);

      try {
        const response = await Api.OUTREACH.DELETE_EMAIL_SIGNATURE.delete({
          urlArgs: { id },
        });

        if (response && response.id) {
          // Update signatures list
          const updatedSignaturesList = signatures.filter((signature) => signature.id !== response.id);
          setSignatures(updatedSignaturesList);

          // Reset default signature if deleted
          if (response.default) {
            setDefaultSignature(noSignature);
          }

          // Reset selected signature if deleted
          if (selectedSignature.id === response.id) {
            if (response.default) {
              setSelectedSignature(noSignature);
            } else {
              setSelectedSignature(defaultSignature);
            }
          }

          EverTrue.Alert.success('Email Signature deleted successfully');
        }

        return response;
      } catch (e) {
        ErrorLogger.error('Error deleting Email Signature', { extra: { e, page_name: 'Email Signatures Context' } });
        setError('Failed to delete email signature');
        EverTrue.Alert.error('There was a problem deleting the email signature.');
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [signatures, selectedSignature.id, defaultSignature]
  );

  const setAsDefault = async (id) => {
    try {
      // Clear default on all others, set on the target
      const updated = await Api.OUTREACH.UPDATE_EMAIL_SIGNATURE.patch({
        urlArgs: { id },
        data: JSON.stringify({ default: true }),
      });
      setSignatures((prev) =>
        prev.map((sig) => {
          if (sig.id === id) return updated;
          if (sig.default) return { ...sig, default: false };
          return sig;
        })
      );
      setDefaultSignature(updated);
      EverTrue.Alert.success('Default signature updated');
      return updated;
    } catch (e) {
      ErrorLogger.error('Error setting default signature', { extra: { e, page_name: 'Email Signatures Context' } });
      EverTrue.Alert.error('Failed to set default signature.');
      throw e;
    }
  };

  const duplicateSignature = async (signature) => {
    try {
      const newSig = await Api.OUTREACH.EMAIL_SIGNATURE.post({
        data: JSON.stringify({
          signature_name: `${signature.signature_name} (Copy)`,
          signature_body: signature.signature_body || '',
          default: false,
        }),
      });
      setSignatures((prev) => {
        const withoutNo = prev.filter((s) => s.id !== 0);
        return [...withoutNo, newSig, noSignature];
      });
      setSelectedSignature(newSig);
      EverTrue.Alert.success('Signature duplicated');
      return newSig;
    } catch (e) {
      ErrorLogger.error('Error duplicating signature', { extra: { e, page_name: 'Email Signatures Context' } });
      EverTrue.Alert.error('Failed to duplicate signature.');
      throw e;
    }
  };

  // Initialize signatures on mount
  useEffect(() => {
    getEmailSignatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle signature selection
  const handleSelectSignature = (signature) => {
    if (!signature || signature.id === selectedSignature.id) return;
    setSelectedSignature(signature);
  };

  const contextValue = {
    // States
    signatures,
    selectedSignature,
    defaultSignature,
    loading,
    error,
    // Actions
    setSelectedSignature: handleSelectSignature,
    refreshSignatures: getEmailSignatures,
    addEmailSignature,
    updateEmailSignature,
    deleteEmailSignature,
    setAsDefault,
    duplicateSignature,
  };

  return <EmailSignaturesContext.Provider value={contextValue}>{children}</EmailSignaturesContext.Provider>;
};

const useEmailSignaturesProvider = () => {
  const context = useContext(EmailSignaturesContext);
  if (!context) {
    throw new Error('useEmailSignaturesProvider must be used within a EmailSignaturesProvider');
  }
  return context;
};

EmailSignaturesProvider.propTypes = { children: PropTypes.node, scope: PropTypes.string };

export { EmailSignaturesProvider, EmailSignaturesContext, useEmailSignaturesProvider };
